import { html, LitElement, unsafeCSS, type CSSResultGroup, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { DEFAULT_LOGO_SRC } from '@/navbar/constants'
import { localiseLabel, localiseMarketingUrl } from '@/navbar/utils'
import { $t, localized } from '@/navbar/i18n'
import type { NavbarAppSwitcherTaxonomyCapability, NavbarAppSwitcherSimpleCapability } from '@/navbar/navbar.types'

// Styles:
import styles from './eco-suite-navbar-app-switcher-ad.scss?inline'

/**
 * @internal
 */
@localized()
@customElement('eco-suite-navbar-app-switcher-ad')
export class EcoSuiteNavbarAppSwitcherAd extends LitElement {
  static readonly styles: CSSResultGroup = [unsafeCSS(styles)]

  @property({ attribute: false })
  headerText?: string

  @property({ attribute: false })
  bodyText?: string

  @property({ attribute: false })
  taxonomyCapabilities: NavbarAppSwitcherTaxonomyCapability[] = []

  /**
   * Optional list of simple capabilities to display in place of `taxonomyCapabilities`.
   * These are typically overridden at runtime via Pendo to surface targeted upsell content.
   * When non-empty, takes precedence over `taxonomyCapabilities`.
   */
  @property({ attribute: false })
  adCapabilities: NavbarAppSwitcherSimpleCapability[] = []

  @property({ attribute: false })
  exploreMoreUrl?: string

  @property({ attribute: false })
  exploreMoreText?: string

  get capabilities(): NavbarAppSwitcherSimpleCapability[] | NavbarAppSwitcherTaxonomyCapability[] {
    return this.adCapabilities.length ? this.adCapabilities : this.taxonomyCapabilities
  }

  render() {
    return html`
      <img src=${DEFAULT_LOGO_SRC} alt="" aria-hidden="true" />
      <div class="AppSwitcherAd__header">
        <p role="heading" aria-level="3">${this.headerText}</p>
        <p>${this.bodyText}</p>
      </div>

      ${this.capabilities.length ? html`
        <div class="AppSwitcherAd__capabilities">
          <p class="AppSwitcherAd__capabilities-heading">${$t('appSwitcher.additionalCapabilities')}</p>
          <div class="AppSwitcherAd__chips">
            ${this.capabilities.map(c => {
              const isSimpleCapability = Object.hasOwn(c, 'marketing_url') && Object.hasOwn(c, 'label')
              const label = isSimpleCapability
                ? (c as NavbarAppSwitcherSimpleCapability).label
                : (localiseLabel((c as NavbarAppSwitcherTaxonomyCapability).labels) ?? '')
              const href = isSimpleCapability
                ? (c as NavbarAppSwitcherSimpleCapability).marketing_url
                : (localiseMarketingUrl((c as NavbarAppSwitcherTaxonomyCapability).marketing_urls) ?? '')

              return html`
                <a
                  class="AppSwitcherAd__chip"
                  href=${href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="${label}, ${$t('navbar.opensInNewTab')}"
                >
                  ${label}
                </a>
              `
            })}
            <span class="AppSwitcherAd__chips-more">${$t('appSwitcher.andManyMore')}...</span>
          </div>
        </div>
      ` : nothing}
      <a
        class="AppSwitcherAd__cta"
        href=${this.exploreMoreUrl ?? ''}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="${this.exploreMoreText || $t('appSwitcher.exploreMoreFeatures')}, ${$t('navbar.opensInNewTab')}"
      >
        <span>${this.exploreMoreText || $t('appSwitcher.exploreMoreFeatures')}</span>
        <pwc-icon-shadow icon="external-link" size="xs" aria-hidden="true"></pwc-icon-shadow>
      </a>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'eco-suite-navbar-app-switcher-ad': EcoSuiteNavbarAppSwitcherAd
  }
}
